import { type Column, DataTable } from '@/components/common/DataTable';
import { cn, formatCurrency, formatNumber, formatPercent } from '@/lib/utils';
import type { Position } from '@/types';

interface PositionsTableProps {
  positions: Position[];
  isLoading?: boolean;
  onPositionClick?: (position: Position) => void;
  className?: string;
}

function PnlCell({ value, percent }: { value: number; percent: number }) {
  const isPositive = value >= 0;
  return (
    <div className={cn('font-medium', isPositive ? 'text-green-600' : 'text-red-600')}>
      <div>{formatCurrency(value)}</div>
      <div className="text-xs">{formatPercent(percent)}</div>
    </div>
  );
}

function DayChangeCell({
  currentPrice,
  avgEntryPrice,
}: {
  currentPrice: number;
  avgEntryPrice: number;
}) {
  const change = currentPrice - avgEntryPrice;
  const changePct = avgEntryPrice > 0 ? (change / avgEntryPrice) * 100 : 0;
  const isPositive = change >= 0;
  return (
    <div className={cn('font-medium', isPositive ? 'text-green-600' : 'text-red-600')}>
      <div>{formatCurrency(change)}</div>
      <div className="text-xs">{formatPercent(changePct)}</div>
    </div>
  );
}

export function PositionsTable({
  positions,
  isLoading,
  onPositionClick,
  className,
}: PositionsTableProps) {
  const columns: Column<Position>[] = [
    {
      header: 'Symbol',
      accessor: 'symbol',
      render: (value) => <span className="font-semibold text-primary">{String(value)}</span>,
    },
    {
      header: 'Quantity',
      accessor: 'quantity',
      render: (value, row) => (
        <div>
          <span className="font-medium">{formatNumber(value as number, 0)}</span>
          <span className="text-xs text-muted-foreground ml-1">({row.side})</span>
        </div>
      ),
      className: 'text-right',
      headerClassName: 'text-right',
    },
    {
      header: 'Avg Cost',
      accessor: 'avg_entry_price',
      render: (value) => formatCurrency(value as number),
      className: 'text-right',
      headerClassName: 'text-right',
    },
    {
      header: 'Current Price',
      accessor: 'current_price',
      render: (value) => formatCurrency(value as number),
      className: 'text-right',
      headerClassName: 'text-right',
    },
    {
      header: 'Day Change',
      accessor: 'current_price',
      render: (_, row) => (
        <DayChangeCell currentPrice={row.current_price} avgEntryPrice={row.avg_entry_price} />
      ),
      className: 'text-right',
      headerClassName: 'text-right',
    },
    {
      header: 'Value',
      accessor: 'market_value',
      render: (value) => <span className="font-medium">{formatCurrency(value as number)}</span>,
      className: 'text-right',
      headerClassName: 'text-right',
    },
    {
      header: 'P&L',
      accessor: 'unrealized_pnl',
      render: (_, row) => (
        <PnlCell value={row.unrealized_pnl} percent={row.unrealized_pnl_percent} />
      ),
      className: 'text-right',
      headerClassName: 'text-right',
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={positions}
      loading={isLoading}
      emptyMessage="No open positions. Your portfolio is all cash."
      className={className}
      rowKey="id"
      onRowClick={onPositionClick}
    />
  );
}
